import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { Tabbar, type TabbarProps } from '@widgets/tabbar';

import { BottomSheetConfig, TabbarContext } from './useTabbar';

interface TabbarProviderProps extends TabbarProps {
  children: React.ReactNode;
}

export const TabbarProvider = ({ children }: TabbarProviderProps) => {
  const [showNavigation, setShowNavigation] = useState<boolean>(true);
  const [showBottomSheet, setShowBottomSheet] = useState<boolean>();
  const [bottomSheet, setBottomSheetState] = useState<React.ReactNode>();
  const [, setBottomSheetConfig] = useState<BottomSheetConfig>({});
  const bottomSheetRef = useRef<ReactNode>();

  const setBottomSheet = useCallback(
    (node: ReactNode, config?: BottomSheetConfig) => {
      bottomSheetRef.current = node;
      setBottomSheetState(node);
      config && setBottomSheetConfig(config);
    },
    [],
  );

  // One object for the provider's whole life. A value carrying the sheet would
  // change on every setBottomSheet, re-render every consumer, and loop any
  // effect that sets a sheet and lists `tabbar` in its deps.
  const value = useMemo(
    () => ({
      setShowNavigation,
      setShowBottomSheet,
      setBottomSheet,
      getBottomSheet: () => bottomSheetRef.current,
    }),
    [setBottomSheet],
  );

  return (
    <TabbarContext.Provider value={value}>
      {children}
      <Tabbar
        showNavigation={showNavigation}
        showBottomSheet={showBottomSheet}
        bottomSheet={bottomSheet}
      />
    </TabbarContext.Provider>
  );
};
